import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Bot, Plus, Trash2, Loader2, Save, Sparkles, Radio, Link2, BarChart3, RefreshCw, ExternalLink, Mail, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PLATFORMS = ["twitter", "facebook", "linkedin", "instagram", "youtube"];
const CONNECTABLE = ["facebook", "linkedin", "youtube", "instagram", "twitter"];

export default function SelfPromoPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newAcct, setNewAcct] = useState({ platform: "twitter", zernio_account_id: "", label: "" });
  const [analytics, setAnalytics] = useState(null);
  const [zernioAccts, setZernioAccts] = useState(null);
  const [connecting, setConnecting] = useState("");
  const [pending, setPending] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [domains, setDomains] = useState(null);
  const [preview, setPreview] = useState(null);

  const openPreview = async () => {
    try {
      const r = await api.get("/promo/digest/preview");
      setPreview(r.data.html || "<p style='color:#9CA3AF;font-family:sans-serif;padding:20px'>No pending promos to preview.</p>");
    } catch (e) { toast.error("Could not load preview."); }
  };

  const load = () => api.get("/promo/config").then((r) => setCfg(r.data)).catch(() => {});
  const loadAnalytics = () => api.get("/promo/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadAnalytics();
    api.get("/promo/pending").then((r) => setPending(r.data)).catch(() => {});
    api.get("/promo/domains").then((r) => setDomains(r.data)).catch(() => {});
  }, []);

  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const addAccountObj = (acct) => {
    if ((cfg.accounts || []).length >= 2) { toast.error("Max 2 accounts for self-promotion."); return; }
    if ((cfg.accounts || []).some((a) => a.zernio_account_id === acct.zernio_account_id)) { toast.info("Already added."); return; }
    set("accounts", [...(cfg.accounts || []), acct]);
  };
  const addAccount = () => {
    if (!newAcct.zernio_account_id) { toast.error("Enter a Zernio account ID."); return; }
    addAccountObj({ ...newAcct });
    setNewAcct({ platform: "twitter", zernio_account_id: "", label: "" });
  };
  const removeAccount = (i) => set("accounts", cfg.accounts.filter((_, idx) => idx !== i));

  const connect = async (platform) => {
    setConnecting(platform);
    try {
      const r = await api.get(`/promo/connect/${platform}`);
      if (r.data.authUrl) {
        window.open(r.data.authUrl, "_blank", "noopener");
        toast.success(`Opened ${platform} connect — authorize, then click "Refresh connected".`);
      } else {
        toast.error(r.data.error || "Could not start connect.");
        if (r.data.dashboard_url) window.open(r.data.dashboard_url, "_blank", "noopener");
      }
    } catch (e) {
      toast.error("Connect failed.");
    } finally { setConnecting(""); }
  };

  const refreshZernio = async () => {
    try {
      const r = await api.get("/promo/accounts");
      setZernioAccts(r.data);
      if (!r.data.length) toast.info("No connected accounts yet on Zernio.");
    } catch (e) { toast.error("Could not fetch Zernio accounts."); }
  };

  const loadPending = () => api.get("/promo/pending").then((r) => setPending(r.data)).catch(() => {});
  const approve = async (id) => {
    try { await api.post(`/promo/${id}/approve`); toast.success("Approved."); loadPending(); }
    catch (e) { toast.error("Approve failed."); }
  };
  const reject = async (id) => {
    try { await api.post(`/promo/${id}/reject`); toast.success("Rejected."); loadPending(); }
    catch (e) { toast.error("Reject failed."); }
  };
  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/promo/digest/send");
      if (r.data.sent) toast.success(`Digest sent (${r.data.count} pending).`);
      else toast.error(`Not sent: ${r.data.reason}`);
    } catch (e) { toast.error("Digest send failed."); }
    finally { setSendingDigest(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/promo/config", {
        enabled: cfg.enabled,
        generate_images: cfg.generate_images,
        accounts: cfg.accounts || [],
        interval_hours: Number(cfg.interval_hours) || 12,
        schedule_mode: cfg.schedule_mode || "interval",
        schedule_days: cfg.schedule_days || [0, 1, 2, 3, 4],
        schedule_times: cfg.schedule_times || [],
        require_approval: cfg.require_approval,
        auto_approve_high_hook: cfg.auto_approve_high_hook,
        digest_enabled: cfg.digest_enabled,
        digest_recipient: cfg.digest_recipient || null,
        digest_from: cfg.digest_from || null,
      });
      setCfg(r.data);
      toast.success("Self-promotion settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/promo/generate?count=1");
      const doc = r.data.created?.[0];
      const msg = doc?.dispatched ? "Generated & published to Zernio."
        : doc?.status === "approved" ? "Generated & auto-approved (high hook score)."
        : "Fresh promo generated — added to pending approval.";
      toast.success(msg);
      loadAnalytics();
      loadPending();
    } catch (e) {
      toast.error("Generation failed.");
    } finally { setGenerating(false); }
  };

  return (
    <div className="rounded-xl border border-accent/30 bg-card p-6 space-y-5" data-testid="self-promo-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><Bot className="h-4 w-4 text-accent" /> Self-Promotion Engine</h3>
          <p className="text-xs text-muted-foreground mt-1">AutoGridSocial markets itself on autopilot. Internal — not a customer feature.</p>
        </div>
        <Badge variant="outline" className={cfg.enabled ? "border-emerald-500/40 text-emerald-400" : "border-border text-muted-foreground"}>
          <Radio className="h-3 w-3 mr-1" /> {cfg.enabled ? "Auto-publish ON" : "Generate only"}
        </Badge>
      </div>

      <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 p-3">
        <div>
          <div className="text-sm font-medium">Auto-publish to Zernio</div>
          <div className="text-xs text-muted-foreground">Publish generated promos to the accounts below</div>
        </div>
        <Switch data-testid="promo-enabled-toggle" checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", v)} />
      </div>

      <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 p-3">
        <div>
          <div className="text-sm font-medium">Attach Flux graphic</div>
          <div className="text-xs text-muted-foreground">Generate an image for each promo (needs Replicate credit)</div>
        </div>
        <Switch data-testid="promo-images-toggle" checked={!!cfg.generate_images} onCheckedChange={(v) => set("generate_images", v)} />
      </div>

      <div className="rounded-lg border border-border bg-secondary/20 p-4 space-y-4" data-testid="promo-schedule-section">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Posting schedule</div>
            <div className="text-xs text-muted-foreground">When the engine generates &amp; publishes a fresh promo</div>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-border bg-secondary/40 p-0.5">
            <button data-testid="schedule-mode-interval" onClick={() => set("schedule_mode", "interval")}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${(cfg.schedule_mode || "interval") === "interval" ? "bg-primary text-white" : "text-muted-foreground"}`}>Every N hours</button>
            <button data-testid="schedule-mode-times" onClick={() => set("schedule_mode", "times")}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${cfg.schedule_mode === "times" ? "bg-primary text-white" : "text-muted-foreground"}`}>Specific times</button>
          </div>
        </div>

        {(cfg.schedule_mode || "interval") === "interval" ? (
          <div className="space-y-2 max-w-[200px]">
            <Label className="text-xs">Generate every (hours)</Label>
            <Input data-testid="promo-interval-input" type="number" min={1} value={cfg.interval_hours}
              onChange={(e) => set("interval_hours", e.target.value)} />
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Days (UTC)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, idx) => {
                  const active = (cfg.schedule_days || []).includes(idx);
                  return (
                    <button key={d} type="button" data-testid={`schedule-day-${idx}`}
                      onClick={() => set("schedule_days", active
                        ? (cfg.schedule_days || []).filter((x) => x !== idx)
                        : [...(cfg.schedule_days || []), idx].sort((a, b) => a - b))}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${active ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs">Times (UTC)</Label>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="schedule-times-list">
                {(cfg.schedule_times || []).map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/15 text-primary text-xs font-mono" data-testid={`schedule-time-${t}`}>
                    {t}
                    <button type="button" onClick={() => set("schedule_times", (cfg.schedule_times || []).filter((_, idx) => idx !== i))}
                      className="hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
                  </span>
                ))}
                <input type="time" data-testid="schedule-time-input"
                  className="bg-secondary/40 border border-border rounded-md px-2 py-1 text-xs"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && !(cfg.schedule_times || []).includes(v)) set("schedule_times", [...(cfg.schedule_times || []), v].sort());
                    e.target.value = "";
                  }} />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Pick a time to add it. All times are UTC.</p>
            </div>
          </div>
        )}
      </div>

      {/* Product's own Zernio accounts */}
      <div className="space-y-2">
        <Label>Product's own Zernio accounts</Label>
        {(cfg.accounts || []).map((a, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 p-3" data-testid={`promo-account-${i}`}>
            <Badge className="bg-primary/15 text-primary border-primary/30 capitalize">{a.platform}</Badge>
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{a.label || a.zernio_account_id}</div>
              <div className="text-xs font-mono text-muted-foreground truncate">{a.zernio_account_id}</div>
            </div>
            <Button size="icon" variant="ghost" className="text-rose-400 h-8 w-8" data-testid={`promo-remove-account-${i}`} onClick={() => removeAccount(i)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {(cfg.accounts || []).length < 2 && (
          <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr_1fr_auto] gap-2 items-end pt-1">
            <Select value={newAcct.platform} onValueChange={(v) => setNewAcct((n) => ({ ...n, platform: v }))}>
              <SelectTrigger data-testid="promo-new-platform"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="promo-new-account-id" value={newAcct.zernio_account_id} onChange={(e) => setNewAcct((n) => ({ ...n, zernio_account_id: e.target.value }))} placeholder="acc_xxx" />
            <Input data-testid="promo-new-account-label" value={newAcct.label} onChange={(e) => setNewAcct((n) => ({ ...n, label: e.target.value }))} placeholder="@handle" />
            <Button data-testid="promo-add-account" onClick={addAccount} variant="outline" className="border-primary/40 text-primary"><Plus className="h-4 w-4" /></Button>
          </div>
        )}
      </div>

      {/* Connect via Zernio */}
      <div className="space-y-3 pt-2 border-t border-border">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2"><Link2 className="h-4 w-4 text-accent" /> Connect the product's own accounts</Label>
          <Button data-testid="promo-refresh-accounts" onClick={refreshZernio} variant="ghost" size="sm" className="text-xs text-primary">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh connected
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {CONNECTABLE.map((p) => (
            <Button key={p} data-testid={`promo-connect-${p}`} onClick={() => connect(p)} disabled={connecting === p}
              variant="outline" size="sm" className="capitalize border-border">
              {connecting === p ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />}
              {p}
            </Button>
          ))}
        </div>
        {zernioAccts && (
          <div className="space-y-2">
            {zernioAccts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No accounts connected on Zernio yet. Connect one above, authorize, then refresh.</p>
            ) : zernioAccts.map((a) => (
              <div key={a.zernio_account_id} className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2.5" data-testid={`zernio-account-${a.zernio_account_id}`}>
                <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/40 capitalize">{a.platform}</Badge>
                <span className="text-sm truncate flex-1 min-w-0">{a.label || a.zernio_account_id}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs border-primary/40 text-primary"
                  data-testid={`use-zernio-account-${a.zernio_account_id}`} onClick={() => addAccountObj(a)}>
                  <Plus className="h-3 w-3 mr-1" /> Use
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approval + Weekly digest */}
      <div className="space-y-4 pt-2 border-t border-border">
        <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 p-3">
          <div>
            <div className="text-sm font-medium">Require approval before publishing</div>
            <div className="text-xs text-muted-foreground">New promos are held as "pending" until you approve them</div>
          </div>
          <Switch data-testid="promo-approval-toggle" checked={!!cfg.require_approval} onCheckedChange={(v) => set("require_approval", v)} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 p-3">
          <div>
            <div className="text-sm font-medium">Auto-approve high hooks (9–10)</div>
            <div className="text-xs text-muted-foreground">Strong posts skip the queue and publish automatically</div>
          </div>
          <Switch data-testid="promo-autoapprove-toggle" checked={!!cfg.auto_approve_high_hook} onCheckedChange={(v) => set("auto_approve_high_hook", v)} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 p-3">
          <div>
            <div className="text-sm font-medium">Weekly digest email</div>
            <div className="text-xs text-muted-foreground">Emails pending promos every Monday with approve links</div>
          </div>
          <Switch data-testid="promo-digest-toggle" checked={!!cfg.digest_enabled} onCheckedChange={(v) => set("digest_enabled", v)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Digest recipient</Label>
            <Input data-testid="promo-digest-recipient" type="email" value={cfg.digest_recipient || ""}
              onChange={(e) => set("digest_recipient", e.target.value)} placeholder="you@example.com" />
          </div>
          <Button data-testid="promo-send-digest" onClick={sendDigest} disabled={sendingDigest} variant="outline" className="border-accent/40 text-accent">
            {sendingDigest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} Send test digest
          </Button>
        </div>
        <Button data-testid="promo-preview-digest" onClick={openPreview} variant="ghost" size="sm" className="text-primary">
          <Eye className="h-4 w-4 mr-2" /> Preview digest email
        </Button>
        <p className="text-xs text-muted-foreground">Note: Resend sandbox only delivers to your own Resend-account email until you verify a domain.</p>

        {/* Sending domain / from-address */}
        <div className="space-y-2 rounded-lg border border-border bg-secondary/20 p-3">
          <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-accent" /> Sending domain (from-address)</Label>
          <Input data-testid="promo-digest-from" value={cfg.digest_from || ""} onChange={(e) => set("digest_from", e.target.value)}
            placeholder="AutoGridSocial <hello@yourdomain.com>" />
          {domains && (domains.restricted || (domains.domains || []).length === 0) ? (
            <p className="text-xs text-muted-foreground">
              To send from your own domain (and reach any inbox), verify a domain in Resend, then use an address on it above.{" "}
              <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" data-testid="verify-domains-link" className="text-primary underline inline-flex items-center gap-0.5">
                Verify a domain <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          ) : domains ? (
            <div className="flex flex-wrap gap-2" data-testid="verified-domains">
              {(domains.domains || []).map((d) => (
                <Badge key={d.id} variant="outline" className={d.status === "verified" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}>
                  {d.name} · {d.status}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {/* Pending approvals */}
      {pending.length > 0 && (
        <div className="space-y-3 pt-2 border-t border-border">
          <Label className="flex items-center gap-2"><Radio className="h-4 w-4 text-amber-400" /> Pending approval ({pending.length})</Label>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {pending.map((p) => (
              <div key={p.id} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid={`pending-promo-${p.id}`}>
                <div className="flex items-center gap-2 mb-2 text-xs font-mono uppercase text-muted-foreground">
                  <span>{p.platform}</span><span>·</span><span>{p.angle_used}</span>
                  {typeof p.hook_score === "number" && <span className="ml-auto">Hook {p.hook_score}/10</span>}
                </div>
                <p className="text-sm text-foreground/90 line-clamp-3 mb-3">{p.post_text}</p>
                <div className="flex items-center gap-2">
                  <Button size="sm" data-testid={`approve-promo-${p.id}`} onClick={() => approve(p.id)} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white">Approve</Button>
                  <Button size="sm" variant="ghost" data-testid={`reject-promo-${p.id}`} onClick={() => reject(p.id)} className="h-7 text-xs text-rose-400">Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Angle analytics */}
      {analytics && (
        <div className="space-y-3 pt-2 border-t border-border">
          <Label className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Angle performance</Label>
          <p className="text-xs text-muted-foreground">The engine auto-favors the angle that drives the most trial signups.</p>
          <div className="space-y-1.5">
            {analytics.per_angle.map((s) => {
              const max = Math.max(1, ...analytics.per_angle.map((x) => x.clicks));
              return (
                <div key={s.angle} className="flex items-center gap-3" data-testid={`angle-stat-${s.angle}`}>
                  <div className="w-40 shrink-0 flex items-center gap-1.5">
                    <span className="text-xs font-mono truncate">{s.angle}</span>
                    {analytics.favored === s.angle && (
                      <Badge className="bg-primary/15 text-primary border-primary/30 text-[9px] px-1.5 py-0">Favored</Badge>
                    )}
                  </div>
                  <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary/60" style={{ width: `${(s.clicks / max) * 100}%` }} />
                  </div>
                  <span className="text-[11px] font-mono text-muted-foreground w-24 text-right">{s.clicks} clk · {s.signups} sign</span>
                </div>
              );
            })}
          </div>

          {/* A/B win-rate table */}
          <div className="rounded-lg border border-border overflow-hidden mt-3" data-testid="ab-winrate-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-secondary/40 text-muted-foreground">
                  <th className="text-left font-medium px-3 py-2">Angle</th>
                  <th className="text-right font-medium px-3 py-2">Clicks</th>
                  <th className="text-right font-medium px-3 py-2">Signups</th>
                  <th className="text-right font-medium px-3 py-2">Win rate</th>
                </tr>
              </thead>
              <tbody>
                {[...analytics.per_angle]
                  .map((s) => ({ ...s, wr: s.clicks ? Math.min(100, (s.signups / s.clicks) * 100) : 0 }))
                  .sort((a, b) => b.wr - a.wr)
                  .map((s) => (
                    <tr key={s.angle} className="border-t border-border" data-testid={`winrate-row-${s.angle}`}>
                      <td className="px-3 py-2 font-mono">
                        {s.angle}
                        {analytics.favored === s.angle && <span className="ml-1.5 text-primary">★</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{s.clicks}</td>
                      <td className="px-3 py-2 text-right font-mono">{s.signups}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        <span className={s.wr >= 30 ? "text-emerald-400" : s.wr >= 10 ? "text-amber-400" : "text-muted-foreground"}>
                          {s.wr.toFixed(0)}%
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        <Button data-testid="promo-save-button" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save
        </Button>
        <Button data-testid="promo-generate-now" onClick={generateNow} disabled={generating} variant="outline" className="border-accent/40 text-accent">
          {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Generate now
        </Button>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle>Weekly digest preview</DialogTitle>
            <DialogDescription>Exactly how the approval email will look in your inbox.</DialogDescription>
          </DialogHeader>
          <iframe title="digest-preview" srcDoc={preview || ""} className="w-full h-[60vh] rounded-lg border border-border bg-white" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
